import { Col, Row, Tooltip } from 'antd';
import React, { FC, useState } from 'react';
import styled from 'styled-components';
import ContentStore from '../../../../stores/ContentStore';
import { Transaction } from '../../../../swagger/api';
import timeFormatting from '../../../../utils/timeFormatting';
import dateFormatting from '../../../../utils/dateFormatting';
import { StyledTableLabel } from '../../../../layout/newGlobalFonts';
import { Typography } from '../../../../layout/components/Typography';
import { overlayInnerStyle } from '../../../../common-components/TooltipStyling';
import { ReactComponent as BizzIcon } from '../../../../layout/icons/payment/bizz-big-less-white-space.svg';
import { ReactComponent as LicensePlateIcon } from '../../../../layout/icons/payment/license-plate-big-less-white-space.svg';
import { ReactComponent as IsNotPaidIcon } from '../../../../layout/icons/red-circle-exclamation-small.svg';
import { ReactComponent as GreenBadgeStatusIcon } from '../../../../layout/icons/green-circle-exclamation-small.svg';
import decimalFormatting from '../../../../utils/decimalFormatting';
import LoadingSpinner from '../../../../common-components/LoadingSpinner';
import { isANPR, isBizz } from '../../../../utils/productTypeUtilities';
import { ColorScheme } from '../../../../layout/colorScheme';
import {
  getInvoiceStatusDisplayInfo,
  getTransactionDisplayAmount,
  getTransactionLinkDisplayInfo,
  isPaidStatus
} from '../../../../utils/invoiceStatusUtils';
import getDisplayTollChargerName from '../../../../utils/getDisplayTollChargerName';
import getProductOrReferenceLabel from '../../../../utils/getProductOrReferenceLabel';

const ColoredRow = styled(Row)<{
  iseven: boolean;
  isoutstanding?: boolean;
  iscredit?: boolean;
  $rowBorderTop?: string;
}>`
  ${(props) => {
    if (props.isoutstanding) {
      return `background-color: ${ColorScheme.OutstandingBackground};`;
    }
    if (props.iscredit) {
      return `background-color: ${ColorScheme.CreditBackground};`;
    }
    return props.iseven
      ? `background-color: #${ColorScheme.TableGrey};`
      : 'background-color: white;';
  }}
  ${(props) => props.$rowBorderTop && `border-top: ${props.$rowBorderTop};`}
  margin: 0px;
  padding: 18px 20px 0px 20px;
  box-sizing: border-box;
`;

const ContentRow = styled(Row)`
  margin-bottom: 15px;
`;

const FlexEndColumn = styled(Col)`
  display: flex;
  justify-content: flex-end;
`;

const StatusBadge = styled.div<{ backgroundColor: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  background: ${(props) => props.backgroundColor};
  border-radius: 5px;
  padding: 4px 6px;
  min-width: fit-content;
  cursor: pointer;
`;

const StatusIconWrapper = styled.div`
  display: flex;
  align-items: end;
  flex-shrink: 0;
  width: 18px;
  height: 18px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const StatusText = styled(StyledTableLabel)`
  white-space: nowrap;
  text-box-trim: trim-end;
`;

interface MobileTransactionRowProps {
  contentStore: ContentStore;
  transaction: Transaction;
  isEven: boolean;
  rowBorderTop?: string;
  showOutstandingOnly?: boolean;
  isTableOnProductCard?: boolean;
}

const MobileTransactionRow: FC<MobileTransactionRowProps> = (props) => {
  const {
    contentStore,
    transaction,
    isEven,
    rowBorderTop,
    showOutstandingOnly,
    isTableOnProductCard
  } = props;
  const { productItemsStore, productCardStore } = contentStore.rootStore.productStore;
  const { paymentStore } = contentStore.rootStore;
  const [isLoading, setIsLoading] = useState(false);

  const date = transaction.date && dateFormatting(transaction.date);
  const time = transaction.date && timeFormatting(transaction.date);

  const currentReference = getProductOrReferenceLabel(transaction, productItemsStore);

  const productTypeIcon = isANPR(transaction.productType) ? (
    <LicensePlateIcon />
  ) : isBizz(transaction.productType) ? (
    <BizzIcon />
  ) : null;

  const showProductCardModalOnClick = (identifierId: string) => {
    if (!isTableOnProductCard) {
      setIsLoading(true);
      const productInStore = productItemsStore.findProductFromId(identifierId);
      if (productInStore) {
        productItemsStore.product = productInStore;
        productCardStore.openModal();
        setIsLoading(false);
      } else {
        productItemsStore.getProduct(identifierId).then(() => {
          productCardStore.openModal();
          setIsLoading(false);
        });
      }
    }
  };

  const statusInfo = getInvoiceStatusDisplayInfo(
    {
      invoiceStatus: transaction.invoiceStatus as string,
      invoiceType: transaction.invoiceType,
      transactionType: transaction.transactionType,
      revenueOperationType: transaction.revenueOperationType,
      dueDate: transaction.dueDate
    },
    'private'
  );
  const hasInvoiceNumber = Boolean(transaction.invoiceNumber?.trim());
  const { showLink, cmsKey } = getTransactionLinkDisplayInfo(statusInfo, transaction.invoiceType, {
    invoiceStatus: transaction.invoiceStatus as string,
    transactionType: transaction.transactionType,
    context: 'private',
    hasInvoiceNumber
  });
  const canShowLink = hasInvoiceNumber || isPaidStatus(transaction.invoiceStatus as string);
  const downloadInvoiceOrReceipt = transaction.invoiceId && showLink && canShowLink && (
    <ContentRow justify="end">
      <Col>
        <Typography
          variant="contentSmallLink"
          as="button"
          align="left"
          style={{ cursor: 'pointer', border: 'none', background: 'transparent' }}
          color={ColorScheme.BrobizzGreen}
          onClick={() => {
            setIsLoading(true);
            paymentStore
              .openInvoicePdfInNewTab(transaction.invoiceId!)
              .then(() => setIsLoading(false))
              .catch(() => setIsLoading(false));
          }}
        >
          {contentStore.cmsStore.getContent(cmsKey)?.content}
        </Typography>
      </Col>
    </ContentRow>
  );

  const dateAndTime = showOutstandingOnly ? (
    <StyledTableLabel>
      {transaction.dueDate && dateFormatting(transaction.dueDate)}
    </StyledTableLabel>
  ) : (
    <>
      <Row>
        <StyledTableLabel>{date}</StyledTableLabel>
      </Row>
      <Row>
        <StyledTableLabel $color={ColorScheme.Grey6}>{time}</StyledTableLabel>
      </Row>
    </>
  );

  const statusText = contentStore.cmsStore.getContent(statusInfo.cmsKey)?.content;

  const isRedVariant = statusInfo.variant === 'missingPayment' || statusInfo.variant === 'paidOut';
  const isOutstanding = isRedVariant;
  const isCredit = statusInfo.variant === 'creditBalance' || statusInfo.variant === 'deposited';
  const displayAmount = getTransactionDisplayAmount(
    {
      amount: transaction.amount,
      invoiceStatus: transaction.invoiceStatus as string,
      invoiceType: transaction.invoiceType,
      transactionType: transaction.transactionType,
      revenueOperationType: transaction.revenueOperationType,
      dueDate: transaction.dueDate
    },
    'private'
  );

  const statusConfig = {
    text: statusText,
    showBadge: statusInfo.showBadge,
    textColor: isRedVariant
      ? ColorScheme.ErrorRed
      : statusInfo.variant === 'creditBalance' || statusInfo.variant === 'deposited'
      ? ColorScheme.BrobizzGreen
      : ColorScheme.Grey7,
    backgroundColor: statusInfo.showBadge
      ? isRedVariant
        ? ColorScheme.OverdueBackground
        : ColorScheme.CreditBadgeBackground
      : 'transparent',
    icon: isRedVariant ? <IsNotPaidIcon /> : statusInfo.showBadge ? <GreenBadgeStatusIcon /> : null,
    tooltip: contentStore.cmsStore.getContent(statusInfo.cmsKey)?.tooltip
  };

  const amountColor = isRedVariant
    ? ColorScheme.ErrorRed
    : statusInfo.variant === 'creditBalance' || statusInfo.variant === 'deposited'
    ? ColorScheme.BrobizzGreen
    : ColorScheme.Grey7;

  return (
    <ColoredRow
      iseven={isEven}
      isoutstanding={isOutstanding}
      iscredit={isCredit}
      $rowBorderTop={rowBorderTop}
    >
      {isLoading ? (
        <Col style={{ width: '100%' }}>
          <LoadingSpinner size="md" />
        </Col>
      ) : (
        <Col style={{ width: '100%', paddingLeft: '4px' }}>
          <ContentRow style={{ marginBottom: '8px' }} align="top" gutter={[16, 0]}>
            <Col span={5}>
              <Row>{productTypeIcon}</Row>
            </Col>
            <Col span={9}>
              <Row align="middle" style={{ gap: '8px', marginBottom: '4px' }}>
                <Col>
                  <Row>
                    <Typography
                      variant="tableLabel"
                      as="div"
                      align="left"
                      color={ColorScheme.BrobizzGreen}
                      onClick={() => showProductCardModalOnClick(transaction.identifierId!)}
                      style={{ cursor: 'pointer', fontFamily: 'Campton Medium' }}
                    >
                      {currentReference}
                    </Typography>
                  </Row>
                  <Row>
                    <StyledTableLabel
                      $color={ColorScheme.Grey6}
                      style={{ wordBreak: 'break-word' }}
                    >
                      {transaction.identifierNumber}
                    </StyledTableLabel>
                  </Row>
                </Col>
              </Row>
            </Col>
            <FlexEndColumn span={10}>
              <Row style={{ justifyContent: 'flex-end', gap: '9px', marginBottom: '4px' }}>
                {statusConfig.showBadge ? (
                  <Tooltip
                    placement="bottomLeft"
                    title={statusConfig.tooltip ?? undefined}
                    color="#2d2d2d"
                    overlayInnerStyle={overlayInnerStyle}
                    align={{ offset: [95, 0] }}
                  >
                    <StatusBadge backgroundColor={statusConfig.backgroundColor!}>
                      {statusConfig.icon && (
                        <StatusIconWrapper>{statusConfig.icon}</StatusIconWrapper>
                      )}
                      <StatusText $color={statusConfig.textColor}>{statusConfig.text}</StatusText>
                    </StatusBadge>
                  </Tooltip>
                ) : (
                  <StatusText $color={statusConfig.textColor}>{statusConfig.text}</StatusText>
                )}
              </Row>
            </FlexEndColumn>
          </ContentRow>
          <ContentRow style={{ marginBottom: '6px' }} gutter={[16, 0]}>
            <Col span={5}>
              <Row style={{ marginBottom: '4px' }}>{dateAndTime}</Row>
            </Col>
            <Col span={9}>
              <Typography
                style={{ marginBottom: '0px' }}
                variant="contentSmallLink"
                as="p"
                align="left"
                color={ColorScheme.Grey7}
              >
                {getDisplayTollChargerName(transaction.tollChargerName, (key) =>
                  contentStore.cmsStore.getContent(key)
                )}
              </Typography>
              <StyledTableLabel $color={ColorScheme.Grey6}>
                {contentStore.cmsStore.getContent(transaction.description!)?.content}
              </StyledTableLabel>
            </Col>
            <FlexEndColumn span={10}>
              <Row style={{ justifyContent: 'flex-end', marginBottom: '4px' }}>
                <Typography
                  variant="contentSmall"
                  as="p"
                  align="right"
                  color={amountColor}
                  style={{
                    fontFamily: isOutstanding || isCredit ? 'Campton Semi Bold' : 'Campton Book'
                  }}
                >
                  {`${decimalFormatting(displayAmount)} ${transaction.currency}`}
                </Typography>
              </Row>
            </FlexEndColumn>
          </ContentRow>
          {downloadInvoiceOrReceipt}
        </Col>
      )}
    </ColoredRow>
  );
};

export default MobileTransactionRow;

MobileTransactionRow.defaultProps = {
  showOutstandingOnly: false,
  isTableOnProductCard: false,
  rowBorderTop: ''
};
